import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { portCodeField } from './catalog.js';

/**
 * Liman araması (M13f; ADR 0052): seferin güzergâh öğesine (`from`, ara liman, `to`) ya da
 * güzergâh dışına bağlı liman kalışı. Tahmini (ETA/ETD) ve gerçek (ATA/ATD) zamanlar ayrıdır
 * (EMSA Maritime Data Catalogue; Paris MoU 2009/16/AT m. 24); UTC saklanır, yerel saat farkıyla
 * gösterilir. Durum saklanmaz, zamanlardan ve iptalden türer. Saf modül.
 */

/** Liman faaliyetleri, kapalı liste (ortağın formu "Port Activities"; ADR 0052 §1). */
export const PORT_CALL_ACTIVITIES = [
  'loading',
  'discharging',
  'bunkering',
  'fresh_water',
  'provisions',
  'stores',
  'spares',
  'crew_change',
  'psc_inspection',
  'flag_inspection',
  'class_survey',
  'vetting',
  'repairs',
  'waste_disposal',
  'clearance',
  'canal_transit',
  'medical',
  'other',
] as const;
export type PortCallActivity = (typeof PORT_CALL_ACTIVITIES)[number];

/** Kalış yeri türü (ortağın "Berth / Anchorage"; BGM listesinde "Type"). */
export const PORT_CALL_LOCATION_TYPES = ['berth', 'anchorage', 'buoy', 'sts', 'shipyard'] as const;
export type PortCallLocationType = (typeof PORT_CALL_LOCATION_TYPES)[number];

export const PORT_CALL_STATUSES = ['planned', 'ongoing', 'completed', 'cancelled'] as const;
export type PortCallStatus = (typeof PORT_CALL_STATUSES)[number];

/** Güzergâh öğesi anahtarı: `from`, `to` ya da ara limanın kimliği (ADR 0048 §3). */
export const ITINERARY_KEY_PATTERN =
  /^(?:from|to|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const datetime = () => z.iso.datetime(typed('INVALID_DATETIME'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .nullish()
    .transform((value) => (value ? value : null));

const portCallSchema = z
  .object({
    voyageId: z.uuid(typed('INVALID_VOYAGE')).nullish().default(null),
    itineraryKey: z
      .string(typed('INVALID_ITINERARY_KEY'))
      .regex(ITINERARY_KEY_PATTERN, 'INVALID_ITINERARY_KEY')
      .nullish()
      .default(null),
    portCode: portCodeField(),
    portName: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.normalize('NFC').trim())
      .pipe(z.string().min(1, 'REQUIRED').max(200, 'TOO_LONG')),
    country: z
      .string(typed('INVALID_COUNTRY'))
      .transform((value) => value.trim().toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{2}$/, 'INVALID_COUNTRY')),
    locationType: z.enum(PORT_CALL_LOCATION_TYPES, typed('INVALID_OPTION')),
    berth: optionalText(200),
    eta: datetime(),
    etd: datetime(),
    ata: datetime().nullish().default(null),
    atd: datetime().nullish().default(null),
    /** Limanın yerel saat farkı: dakika hassasiyeti, −12:00…+14:00 (ADR 0065 §8). */
    tzOffsetMin: z
      .number(typed('INVALID_TZ_OFFSET'))
      .refine((v) => Number.isInteger(v) && v >= -720 && v <= 840, 'INVALID_TZ_OFFSET'),
    agentName: optionalText(200),
    agentContact: optionalText(200),
    agentPhone: optionalText(60),
    agentEmail: optionalText(200).pipe(
      z
        .string()
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'INVALID_EMAIL')
        .nullable(),
    ),
    activities: z
      .array(z.enum(PORT_CALL_ACTIVITIES, typed('INVALID_OPTION')), typed('INVALID_LIST'))
      .max(PORT_CALL_ACTIVITIES.length, 'TOO_MANY')
      .default([]),
    notes: optionalText(2000),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.itineraryKey !== null && value.voyageId === null) {
      issue('itineraryKey', 'ITINERARY_WITHOUT_VOYAGE');
    }
    // UN/LOCODE biçimli kodun ilk iki harfi ülkedir; WPI numaralı kodun ülkesi listeden
    if (
      value.portCode !== null &&
      !value.portCode.startsWith('WPI') &&
      value.portCode.slice(0, 2) !== value.country
    ) {
      issue('country', 'COUNTRY_MISMATCH');
    }
    if (Date.parse(value.etd) < Date.parse(value.eta)) issue('etd', 'ETD_BEFORE_ETA');
    if (value.atd !== null && value.ata === null) issue('atd', 'ATD_WITHOUT_ATA');
    if (value.atd !== null && value.ata !== null && Date.parse(value.atd) < Date.parse(value.ata)) {
      issue('atd', 'ATD_BEFORE_ATA');
    }
    if (new Set(value.activities).size !== value.activities.length) {
      issue('activities', 'DUPLICATE_ACTIVITY');
    }
  });

export type PortCallInput = z.output<typeof portCallSchema>;

/** Liman aramasının alanları: biçim ve sıra kuralları; sefer, güzergâh ve liman listesi db'de. */
export function parsePortCall(input: unknown): ParseResult<PortCallInput> {
  const result = portCallSchema.safeParse(input);
  if (result.success) return { ok: true, value: result.data };
  return {
    ok: false,
    issues: result.error.issues.map(
      (issue): SchemaIssue => ({ path: issue.path.map(String).join('.'), code: issue.message }),
    ),
  };
}

/**
 * Gerçek zamanlar gemi sunucusunun saatine göre gelecekte olamaz (CLAUDE.md "Zaman"); tahmini
 * zamanlar olabilir. Hatalı alanların listesi (boşsa geçerli).
 */
export function portCallFutureIssues(
  value: Pick<PortCallInput, 'ata' | 'atd'>,
  now: Date,
): SchemaIssue[] {
  return (['ata', 'atd'] as const)
    .filter((field) => {
      const at = value[field];
      return at !== null && Date.parse(at) > now.getTime();
    })
    .map((field) => ({ path: field, code: 'FUTURE_DATE' }));
}

export interface PortCallTimes {
  eta: string;
  etd: string;
  ata: string | null;
  atd: string | null;
  cancelledAt: string | null;
}

/** Durum: iptal > kalkış (completed) > varış (ongoing) > planned (ADR 0052 §1). */
export function portCallStatus(call: Pick<PortCallTimes, 'ata' | 'atd' | 'cancelledAt'>) {
  if (call.cancelledAt !== null) return 'cancelled' as const;
  if (call.atd !== null) return 'completed' as const;
  if (call.ata !== null) return 'ongoing' as const;
  return 'planned' as const;
}

/**
 * Kalış süresi, saat: gerçek (ATA–ATD) varsa o, varış var kalkış yoksa `now`'a kadar, yoksa
 * tahmini (ETA–ETD). `actual` gerçek zamanlardan mı geldiği.
 */
export function portCallDuration(
  call: PortCallTimes,
  now: Date,
): { hours: number; actual: boolean } {
  const hours = (from: string, to: number) =>
    Math.max(0, Math.round(((to - Date.parse(from)) / 3_600_000) * 10) / 10);
  if (call.ata !== null) {
    return {
      hours: hours(call.ata, call.atd !== null ? Date.parse(call.atd) : now.getTime()),
      actual: true,
    };
  }
  return { hours: hours(call.eta, Date.parse(call.etd)), actual: false };
}
